import type { BeforeRequestHook } from "ky";

import { selectIsOptedIntoCookieAuth } from "@/redux/selectors/cookieAuthPilotSelectors";
import store from "@/redux/store";

import { authorizationHookFactory } from "./auth";

// cookie-auth pilot — opt-in (see .ai/auth-cookies/pilot-admin-profile.md). Default is
// legacy header auth; an admin must set the localStorage flag (see
// src/redux/actions/cookieAuthPilot.ts) and refresh to participate. Participation is
// read once at boot into Redux (src/containers/App.js), not re-read from localStorage
// per request — this hook only reads the Redux value.
//
// Reuses authorizationHookFactory for the (now-default) not-opted-in path instead of a
// hand-duplicated fallback, so that path stays byte-identical to httpAuth's own
// behavior (incl. options.context.accessToken support) rather than risking drift.
const legacyAuthorizationHook = authorizationHookFactory(() => localStorage.getItem("token"));

export const authCookieHook: BeforeRequestHook = (hookArgs) => {
  const isOptedIn = selectIsOptedIntoCookieAuth(store.getState());

  if (!isOptedIn) {
    return legacyAuthorizationHook(hookArgs);
  }

  // Opted in: no Authorization header — auth resolves via the id_token cookie.
  // credentials is "same-origin" by default (ky/fetch default), which never sends
  // cookies cross-origin — so credentials:"include" must be applied explicitly here,
  // per-request, rather than instance-wide on httpAuthCookie. That keeps the two auth
  // mechanisms mutually exclusive on the wire: not-opted-in requests never carry a
  // Cookie header (nothing forces credentials:"include" for them), and opted-in
  // requests never carry Authorization. Constructing a new Request is required
  // because Request.prototype.credentials isn't reassignable after construction —
  // returning it from a beforeRequest hook replaces the outgoing request (ky docs).
  const { request } = hookArgs;
  const cookieRequest = new Request(request, { credentials: "include" });

  cookieRequest.headers.set("Accept", "application/json");
  cookieRequest.headers.set("x-location-path-name", window.location.pathname);

  return cookieRequest;
};
